/**
 * iCRAFTZ HomeV3 · Figma-Make homepage integrated 2026-02-05
 *
 * Source: /tmp/figma-import/emergent_handoff/code/HomePage.tsx (Founder-supplied)
 * Assets: /app/frontend/public/homepage-assets/*.png (18 individual PNGs)
 * Route : /home-v3 (staging only; do NOT switch / until Founder approves)
 *
 * Adjustments applied during integration (per INTEGRATION_BRIEF.md):
 *   1. Converted .tsx → .jsx (removed TS type annotations only; no logic changes)
 *   2. Removed <ImageWithFallback> — replaced with plain <img> (8 occurrences)
 *   3. All @/imports/… asset paths → /homepage-assets/*.png (public folder URLs)
 *   4. Every <a href="#…"> anchor → React-Router <Link to="/…"> with the
 *      routing table from INTEGRATION_BRIEF.md § 2
 *   5. screenBlend flag set to `false` for BOTH compass (DISCOVER) and flame
 *      (COMMIT). Circular-parchment wrapper + mix-blend-mode: screen removed —
 *      icons now render bare, matching book / lotus / tree.
 *   6. Medallions in Section 4 use <img src="/homepage-assets/…"> directly
 *      (dynamic import() dropped — no need in a CRA app with public assets).
 *   7. Nothing else altered. Existing pages, backend, DS, auth untouched.
 */

import React from "react";
import { Link } from "react-router-dom";
import Navigation from "../components/homev3/Navigation";
import ParchmentBackground from "../components/ParchmentBackground";

// ─── Route mapping — from INTEGRATION_BRIEF.md § 2 ─────────────────────────
// Every homepage anchor points at a real Emergent route.
const R = {
  home:        "/",
  library:     "/library",
  collections: "/collections",
  mycraftz:    "/mycraftz",
  sanctuary:   "/my-sanctuary",
  bodha:       "/bodha",
  sankalpa:    "/sankalpa",
  sadhana:     "/sadhana",
  siddhi:      "/mycraftz",     // no /siddhi route yet — Founder confirmed remap
  profile:     "/login",        // no /profile route yet — Founder-suggested fallback
};

// ─── Asset URLs (public/ served at site root) ─────────────────────────────
const A = {
  landscape:  "/homepage-assets/asset-1-landscape.png",
  saraswati:  "/homepage-assets/asset-2-saraswati.png",
  rightPanel: "/homepage-assets/asset-3-right-panel.png",
  archCards:  "/homepage-assets/asset-4-arch-cards.png",
  iconCompass:"/homepage-assets/asset-5-icon-compass-1.png",
  iconBook:   "/homepage-assets/asset-5-icon-book-1.png",
  iconFlame:  "/homepage-assets/asset-5-icon-flame-1.png",
  iconLotus:  "/homepage-assets/asset-5-icon-lotus-1.png",
  iconTree:   "/homepage-assets/asset-5-icon-tree-1.png",
  medHabits:  "/homepage-assets/asset-6-medallion-habits-1.png",
  medFocus:   "/homepage-assets/asset-6-medallion-focus-1.png",
  medWealth:  "/homepage-assets/asset-6-medallion-wealth-1.png",
  medMindset: "/homepage-assets/asset-6-medallion-mindset-1.png",
  medLeader:  "/homepage-assets/asset-6-medallion-leadership-1.png",
  medPurpose: "/homepage-assets/asset-6-medallion-purpose-1.png",
  emblem:     "/homepage-assets/asset-logo-emblem.png",
};

// ─── Design tokens (spec-exact) ───────────────────────────────────────────
const DARK_BROWN    = "#2C1810";
const GOLD          = "#C9A84C";
const PARCHMENT_LT  = "#F0E6CC";
const BODY_TEXT     = "#3D2314";
const MUTED_BROWN   = "#8B6347";
const SANSKRIT_TEXT = "#5C3D2E";

const F_DISPLAY = "'Cinzel Decorative', serif";
const F_HEADING = "'Cinzel', serif";
const F_BODY    = "'EB Garamond', serif";
const F_DEVNAG  = "'Noto Serif Devanagari', serif";

/** CSS-only gold ornamental rule — replaces the divider image assets */
function GoldRule({ wide = false }) {
  return (
    <div
      className="flex items-center gap-2"
      style={{ width: wide ? "100%" : "80px", maxWidth: wide ? 320 : 80 }}
    >
      <div style={{ flex: 1, height: "1px", background: `linear-gradient(to right, transparent, ${GOLD})` }} />
      <span style={{ color: GOLD, fontSize: "9px", lineHeight: 1 }}>&#10022;</span>
      <div style={{ flex: 1, height: "1px", background: `linear-gradient(to left, transparent, ${GOLD})` }} />
    </div>
  );
}

// ─── Data ─────────────────────────────────────────────────────────────────
const PANEL_ROWS = [
  { title: "AI-Curated Wisdom Artifacts", desc: "Extracted from the world's best books",     to: "/bodha?book=atomic-habits",    preview: true, hint: "Preview Bodha — read the ideas from Atomic Habits" },
  { title: "Rule of Three",               desc: "Focus on what truly transforms",             to: "/sankalpa?book=atomic-habits", preview: true, hint: "Preview Sankalpa — choose three ideas from Atomic Habits" },
  { title: "CraftPrint™",                 desc: "Your 30-day personalised action plan",       to: "/mycraftz",                    preview: true, hint: "Preview CraftPrint — sample practice cards; sign in to make your own" },
  { title: "Daily Sadhana",               desc: "Build habits. Build identity.",              to: "/mycraftz/stage/sadhana?from=preview", preview: true, hint: "Preview Sadhana — a sample daily practice card" },
  { title: "Siddhi",                      desc: "Transformation, not just progress",  to: "/mycraftz/stage/siddhi?from=preview", preview: true, hint: "Preview Siddhi — a sample retrospective" },
];

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 1 — NAVIGATION (uses shared component from ../components/homev3)
// ═══════════════════════════════════════════════════════════════════════════
// The homepage nav is now the same shared Navigation component used by
// Library / Bodha / Sankalpa / Sadhana. See /components/homev3/Navigation.jsx
// for desktop + mobile behaviour. The local Navigation() and NAV_LINKS were
// removed in the mobile-slice pass — they duplicated the shared component
// verbatim.

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 2 — HERO
// ═══════════════════════════════════════════════════════════════════════════

function HeroSection() {
  return (
    <section
      data-testid="homev3-hero"
      className="homev3-hero relative overflow-hidden"
      style={{ height: 520, minHeight: 460 }}
    >
      <img
        src={A.landscape}
        alt=""
        aria-hidden
        className="homev3-hero__landscape absolute inset-0 w-full h-full"
        style={{ objectFit: "cover", objectPosition: "left center" }}
      />

      <img
        src={A.saraswati}
        alt="Saraswati, goddess of knowledge and arts"
        className="homev3-hero__saraswati absolute bottom-0 pointer-events-none"
        style={{ right: "264px", height: "70%", width: "auto" }}
      />

      <div className="homev3-hero__row absolute inset-0 z-10 flex" style={{ paddingTop: 0 }}>
        {/* Finalized iCRAFTZ wordmark — lockup image with wordmark + tagline
            + lotus divider all baked in. Repositioned (2026-07) from its
            previous inline slot at the top of the left text column to an
            absolute-positioned anchor over the temple-spires-meet-sky area,
            approximately two-thirds across the hero and slightly left of
            Saraswati's head. On mobile (<900px) the absolute positioning
            is neutralized in the responsive stylesheet below and the lockup
            reverts to a normal block at the top of the mobile stack. */}
        <img
          src="/logo/icraftz-logo-final.webp"
          alt="iCRAFTZ — Where You Craft Who You Become"
          data-testid="homev3-hero-wordmark"
          className="homev3-hero__logo-lockup"
          style={{
            position: "absolute",
            top: 24,
            left: "48%",
            width: 460,
            height: "auto",
            zIndex: 15,
            filter: "drop-shadow(0 2px 6px rgba(44,24,16,0.18))",
            pointerEvents: "none",
          }}
        />

        <div
          className="homev3-hero__text flex flex-col justify-start items-center flex-shrink-0"
          style={{
            width: "42%",
            paddingLeft: 64,
            paddingRight: 64,
            paddingTop: 348,
            textAlign: "center",
            // Fix 7 · softly reinforced parchment scrim so the wordmark and
            // tagline read cleanly against the busy painting. Opacity
            // strengthens toward the text column and fades to transparent
            // well before the temple — Saraswati and the mountains remain
            // undimmed. No hard edge, no dark overlay.
            background:
              "linear-gradient(to right, rgba(248,236,206,0.72) 0%, rgba(248,236,206,0.62) 40%, rgba(240,226,182,0.22) 72%, transparent 100%)",
          }}
        >
          <div
            data-testid="homev3-hero-body"
            className="mb-6"
            style={{ display: "none" }}
            aria-hidden="true"
          >
            <p className="m-0">Timeless wisdom from the world&rsquo;s greatest books.</p>
            <p className="m-0">One idea at a time. One habit at a time.</p>
            <p className="m-0">One transformation at a time.</p>
          </div>

          <div className="flex gap-4 justify-center">
            <Link
              to={R.library}
              data-testid="homev3-cta-begin"
              className="no-underline rounded flex-shrink-0"
              style={{
                fontFamily: F_HEADING,
                fontSize: 12,
                letterSpacing: "0.07em",
                padding: "11px 24px",
                background: DARK_BROWN,
                color: PARCHMENT_LT,
                transition: "filter 150ms",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.filter = "brightness(1.22)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.filter = "brightness(1)"; }}
            >
              Begin Your Journey
            </Link>

            <Link
              to={R.library}
              data-testid="homev3-cta-explore"
              className="no-underline rounded flex-shrink-0"
              style={{
                fontFamily: F_HEADING,
                fontSize: 12,
                letterSpacing: "0.07em",
                padding: "11px 24px",
                background: PARCHMENT_LT,
                color: DARK_BROWN,
                border: `1.5px solid ${GOLD}`,
                transition: "filter 150ms",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.filter = "brightness(0.94)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.filter = "brightness(1)"; }}
            >
              Explore Library
            </Link>
          </div>
        </div>

        <div className="homev3-hero__spacer flex-1" />

        {/* ── Mobile-only Saraswati banner (<900px). Contained image
             centred in the mobile stack between buttons and card. Uses
             the same asset as desktop but sized for a phone with no
             horizontal overflow. Display toggled via media query below. */}
        <div className="homev3-hero__saraswati-mobile" data-testid="homev3-hero-saraswati-mobile">
          <img
            src={A.saraswati}
            alt="Saraswati, goddess of knowledge and arts"
            style={{ maxWidth: 220, width: "80%", height: "auto", display: "block" }}
          />
        </div>

        <div
          className="homev3-hero__panel relative flex-shrink-0 flex items-start"
          style={{ width: 220, height: "100%", marginRight: 20, paddingTop: 162 }}
        >
          <div
            className="homev3-hero__panel-inner homev3-hero__card w-full flex flex-col z-10"
            style={{
              padding: "16px 22px 16px",
              background: "rgba(255, 248, 224, 0.88)",
              border: "1px solid rgba(201, 168, 76, 0.35)",
              borderRadius: 12,
              boxShadow: "0 6px 18px rgba(44, 24, 16, 0.16)",
            }}
          >
            {/* Emblem moved to page-root (mirrors LibraryPage placement) —
                see the top-right <img> just after <Navigation /> in the
                page root. Card starts directly with the "YOUR JOURNEY"
                header. */}

            {/* Mobile-only emblem at top of card (<900px). Page-root
                emblem is hidden on mobile, so we surface the brand mark
                inside the card instead. */}
            <div className="homev3-hero__card-emblem-mobile" data-testid="homev3-hero-card-emblem-mobile">
              <img
                src={A.emblem}
                alt="iCRAFTZ emblem — golden book"
                style={{ height: 80, width: "auto", display: "block", pointerEvents: "none" }}
              />
            </div>

            {/* ── Card header — kicker + subtitle. */}
            <div className="mb-3" data-testid="homev3-hero-card-header">
              <p
                className="m-0"
                style={{
                  fontFamily: F_HEADING,
                  fontSize: 10.5,
                  fontWeight: 600,
                  color: DARK_BROWN,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  marginBottom: 6,
                }}
              >
                Your Journey
              </p>
              <p
                className="m-0"
                style={{
                  fontFamily: F_BODY,
                  fontSize: 12,
                  fontStyle: "italic",
                  color: MUTED_BROWN,
                  lineHeight: 1.4,
                }}
              >
                Your personal journey of wisdom, crafted into life.
              </p>
            </div>

            <div className="flex justify-center mb-2">
              <GoldRule />
            </div>

            <div className="flex flex-col gap-3">
              {PANEL_ROWS.map(({ title, desc, to, preview, hint }) => (
                <Link
                  key={title}
                  to={to}
                  title={hint}
                  data-testid={`homev3-panel-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="no-underline block"
                  style={{ paddingLeft: 6, paddingRight: 6, transition: "opacity 150ms" }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.68"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                >
                  <p className="m-0" style={{ fontFamily: F_HEADING, fontSize: 11, fontWeight: 600, color: DARK_BROWN, letterSpacing: "0.04em" }}>
                    {title}
                    {preview && (
                      <span
                        data-testid="homev3-panel-preview-tag"
                        style={{
                          marginLeft: 8,
                          fontFamily: F_HEADING,
                          fontSize: 9,
                          fontWeight: 600,
                          color: GOLD,
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                        }}
                      >
                        Preview
                      </span>
                    )}
                  </p>
                  <p className="m-0" style={{ fontFamily: F_BODY, fontSize: 11, fontStyle: "italic", color: MUTED_BROWN }}>
                    {desc}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile hero (<768px) ─────────────────────────────────────────
             Collapse the desktop side-by-side hero into a clean vertical
             stack: text column on top (wordmark → subtitle → tagline →
             body → CTAs), unlock panel below as a full-width parchment
             card. The Saraswati painting and its landscape backdrop are
             suppressed on mobile because they can't reflow gracefully;
             a soft parchment field replaces them. The right-panel ornate
             pillar image is also suppressed on mobile — the 6 rows sit
             on a plain parchment card with a gold top rule. ─────────── */}
      <style>{`
        /* Base: mobile-only helpers are hidden on desktop. */
        .homev3-hero__saraswati-mobile   { display: none; }
        .homev3-hero__card-emblem-mobile { display: none; }

        @media (max-width: 899px) {
          .homev3-hero {
            height: auto !important;
            min-height: 0 !important;
            background: linear-gradient(180deg, #F5E6C1 0%, #EDD9A3 100%);
          }
          .homev3-hero__landscape,
          .homev3-hero__saraswati,
          .homev3-hero__panel-img {
            display: none !important;
          }
          .homev3-hero__row {
            position: static !important;
            display: block !important;
          }
          /* Neutralize desktop absolute positioning of the logo lockup —
             on mobile it becomes a normal block at the top of the stack,
             sized to fit small screens without horizontal overflow. */
          .homev3-hero__logo-lockup {
            position: static !important;
            display: block !important;
            width: 88% !important;
            max-width: 340px !important;
            margin: 20px auto 8px !important;
            left: auto !important;
            top: auto !important;
          }
          .homev3-hero__text {
            width: 100% !important;
            padding: 8px 20px 24px !important;
            background: transparent !important;
          }
          [data-testid="homev3-hero-wordmark"] {
            font-size: 3.25rem !important;
          }
          [data-testid="homev3-hero-body"] {
            font-size: 15px !important;
            line-height: 1.6 !important;
          }
          .homev3-hero__spacer {
            display: none !important;
          }
          .homev3-hero__panel {
            position: static !important;
            width: 100% !important;
            height: auto !important;
            padding: 0 20px 28px !important;
            margin-right: 0 !important;
            display: block !important;
          }
          .homev3-hero__panel-inner {
            padding: 20px 18px !important;
            display: block !important;
          }
          /* Contained Saraswati banner in the mobile stack, above the card. */
          .homev3-hero__saraswati-mobile {
            display: flex !important;
            justify-content: center;
            padding: 4px 20px 20px;
          }
          /* Emblem inside the mobile card, above the "YOUR JOURNEY" header. */
          .homev3-hero__card-emblem-mobile {
            display: flex !important;
            justify-content: center;
            margin-bottom: 12px;
          }
        }
      `}</style>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 3 — HOW IT WORKS
// (Fix 1 applied — compass + flame `screenBlend: false`; bare-icon render)
// ═══════════════════════════════════════════════════════════════════════════

const STEPS = [
  { icon: A.iconCompass, title: "DISCOVER",  sa: "जिज्ञासा", tr: "(Jigyasa)",  body: "Find ideas worth exploring",   to: R.library  },
  { icon: A.iconBook,    title: "LEARN",     sa: "बोध",      tr: "(Bodha)",    body: "Understand what truly matters", to: R.bodha    },
  { icon: A.iconFlame,   title: "COMMIT",    sa: "संकल्प",   tr: "(Sankalpa)", body: "Choose one idea to live by",    to: R.sankalpa },
  { icon: A.iconLotus,   title: "PRACTISE",  sa: "साधना",    tr: "(Sadhana)",  body: "Apply daily with discipline",   to: R.sadhana  },
  { icon: A.iconTree,    title: "TRANSFORM", sa: "सिद्धि",   tr: "(Siddhi)",   body: "Become who you want to be",     to: R.siddhi   },
];

// ─────────────────────────────────────────────────────────────────────
//  ArchFrame — scalable SVG replacement for /homepage-assets/asset-4-arch-cards.png
//
//  Draws ONE ornate arch silhouette (parchment fill + double gold hairline +
//  bottom-corner scroll accents). Purely decorative — content sits above it
//  as absolutely-positioned children of the parent .hiw-arch container.
//
//  ViewBox is 160 × 200; parent controls actual pixel size via CSS. Trimming
//  the ~44px empty base that lived in the old baked PNG (was 160 × 244) is
//  what tightens the whole How-It-Works row.
//
//  The old PNG carried a decorative flourish/highlight at the arch apex that
//  became visible when the icon+text cluster was recentred. This SVG omits
//  that flourish entirely — the arch peak reads as a clean dome.
// ─────────────────────────────────────────────────────────────────────
function ArchFrame() {
  return (
    <svg
      viewBox="0 0 160 200"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
      style={{ width: "100%", height: "100%", display: "block", position: "absolute", inset: 0 }}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id="archParchment" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#FDF6DA" />
          <stop offset="55%"  stopColor="#F6E7BA" />
          <stop offset="100%" stopColor="#E9D399" />
        </linearGradient>
        <linearGradient id="archGold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#D9B968" />
          <stop offset="100%" stopColor="#A3822F" />
        </linearGradient>
      </defs>

      {/* Outer arch — dome-topped rectangle. */}
      <path
        d="M 4 196 L 4 68 Q 4 4 80 4 Q 156 4 156 68 L 156 196 Z"
        fill="url(#archParchment)"
        stroke="url(#archGold)"
        strokeWidth="1.6"
      />

      {/* Inner double-line — the ornate parchment convention. */}
      <path
        d="M 10 190 L 10 72 Q 10 12 80 12 Q 150 12 150 72 L 150 190 Z"
        fill="none"
        stroke="rgba(201,168,76,0.55)"
        strokeWidth="0.7"
      />

      {/* Bottom-corner scroll flourishes — subtle nod to the old carved feel. */}
      <path d="M 4 188 Q 14 188 14 178"   fill="none" stroke="url(#archGold)" strokeWidth="1.1" strokeLinecap="round" />
      <path d="M 156 188 Q 146 188 146 178" fill="none" stroke="url(#archGold)" strokeWidth="1.1" strokeLinecap="round" />

      {/* Base ornament — tiny gold jewel-dot at each bottom corner. */}
      <circle cx="14"  cy="188" r="1.4" fill="#B8933F" />
      <circle cx="146" cy="188" r="1.4" fill="#B8933F" />
    </svg>
  );
}

// Gold arrow separator between arches — inline SVG (no icon dep needed).
function ArchArrow() {
  return (
    <svg
      width="18"
      height="14"
      viewBox="0 0 18 14"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0, alignSelf: "center", opacity: 0.85 }}
    >
      <path
        d="M 1 7 L 13 7 M 9 3 L 13 7 L 9 11"
        fill="none"
        stroke="#B8933F"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function HowItWorksSection() {
  return (
    <section
      data-testid="homev3-how-it-works"
      style={{ background: "transparent", padding: "40px 48px 48px" }}
    >
      <div style={{ maxWidth: 940, margin: "0 auto" }}>
        {/* Centered marketing intro — moved (2026-07) from the homepage
            hero's left column onto the flat cream band above the arches
            so it reads as a header lead-in for the transformation path
            rather than fighting the mountain/temple painting behind it.
            Semibold italic + DARK_BROWN, no text-shadow needed on the
            calm parchment ground. */}
        <div
          data-testid="homev3-hiw-intro"
          className="text-center"
          style={{
            fontFamily: F_BODY,
            fontSize: 18,
            fontStyle: "italic",
            fontWeight: 600,
            color: DARK_BROWN,
            lineHeight: 1.72,
            marginBottom: 32,
            padding: "0 20px",
          }}
        >
          <p className="m-0">Timeless wisdom from the world&rsquo;s greatest books.</p>
          <p className="m-0">One idea at a time. One habit at a time.</p>
          <p className="m-0">One transformation at a time.</p>
        </div>

        <div className="hiw-title flex items-center justify-center gap-4 mb-12">
          <GoldRule />
          <p
            className="hiw-title__label m-0 text-center"
            style={{ fontFamily: F_HEADING, fontSize: 13, letterSpacing: "0.18em", color: DARK_BROWN, whiteSpace: "nowrap" }}
          >
            HOW IT WORKS &ndash; The Path of Transformation
          </p>
          <GoldRule />
        </div>

        {/* ── Desktop / wide-viewport layout (≥900px): 5 vector arches
             in a flex row with gold arrow separators. Replaces the old
             single fixed-aspect PNG (asset-4-arch-cards.png, 160×244)
             so each arch is now scalable and content-sized (~200 tall
             — trimmed the ~44px empty base baked into the old PNG). ── */}
        <div className="hiw-desktop hiw-strip">
          {STEPS.map((step, idx) => (
            <React.Fragment key={step.title}>
              <Link
                to={step.to}
                data-testid={`homev3-step-${step.title.toLowerCase()}`}
                className="hiw-arch no-underline"
                aria-label={`${step.title} — ${step.tr.replace(/[()]/g, "")}`}
              >
                <ArchFrame />
                <div className="hiw-arch__content">
                  <div className="hiw-arch__icon">
                    <img src={step.icon} alt="" style={{ height: 74, width: "auto", display: "block" }} />
                  </div>
                  <p className="hiw-arch__title m-0" style={{ fontFamily: F_HEADING, color: DARK_BROWN }}>
                    {step.title}
                  </p>
                  <p className="hiw-arch__sa m-0" style={{ fontFamily: F_DEVNAG, color: GOLD }}>
                    {step.sa}
                  </p>
                  <p className="hiw-arch__tr m-0" style={{ fontFamily: F_BODY, color: "#6B4630" }}>
                    {step.tr}
                  </p>
                  <p className="hiw-arch__body m-0" style={{ fontFamily: F_BODY, color: BODY_TEXT }}>
                    {step.body}
                  </p>
                </div>
              </Link>
              {idx < STEPS.length - 1 && <ArchArrow />}
            </React.Fragment>
          ))}
        </div>

        {/* ── Mobile / narrow-viewport layout (<900px): vertical stack of
             5 parchment cards. Icons no longer overlap titles because
             each card owns its own row. Ornate PNG is dropped (it can't
             reflow gracefully below its design width of ~940px). ─────── */}
        <div className="hiw-mobile" data-testid="homev3-how-it-works-mobile">
          {STEPS.map((step, idx) => (
            <React.Fragment key={step.title}>
              <Link
                to={step.to}
                data-testid={`homev3-step-mobile-${step.title.toLowerCase()}`}
                className="hiw-mobile__card no-underline"
              >
                <div className="hiw-mobile__icon">
                  <img src={step.icon} alt="" style={{ height: 56, width: "auto", display: "block" }} />
                </div>
                <div className="hiw-mobile__text">
                  <p className="hiw-mobile__title m-0" style={{ color: DARK_BROWN }}>{step.title}</p>
                  <p className="hiw-mobile__sa m-0" style={{ color: GOLD }}>{step.sa}</p>
                  <p className="hiw-mobile__tr m-0" style={{ color: "#6B4630" }}>{step.tr}</p>
                  <p className="hiw-mobile__body m-0" style={{ color: BODY_TEXT }}>{step.body}</p>
                </div>
              </Link>

              {idx < STEPS.length - 1 && (
                <div className="hiw-mobile__caret" aria-hidden="true">
                  <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1 L7 9 L13 1" stroke={GOLD} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ── Breakpoint toggle + mobile styles ─────────────────────────
             Chosen breakpoint: 900px. This is intentionally WIDER than
             the app-wide 768px used for nav/other slices. Reason: even
             at 844px landscape (iPhone Pro / Plus rotated), the ornate
             5-arch row's fixed 98px icons overlap the titles below,
             because the icon height doesn't shrink with the parent.
             900px keeps every iPhone class (portrait + landscape) on the
             clean stack, while iPad portrait (768) is edge-cased to
             stack too — a benign upgrade. iPad landscape (1024+) and
             desktop keep the ornate strip.                              */}
      <style>{`
        .hiw-desktop { display: flex; }
        .hiw-mobile  { display: none; }

        /* ── Desktop arch strip: 5 arches + 4 arrow separators, evenly
             spaced across the ≤940px content column. Aspect-ratio locks
             each arch to 160:200 so the ornate frame scales cleanly
             without distortion. Content sits absolutely above the SVG. */
        .hiw-strip {
          align-items: stretch;
          justify-content: space-between;
          gap: 4px;
          width: 100%;
        }
        .hiw-arch {
          position: relative;
          flex: 1 1 0;
          max-width: 172px;
          aspect-ratio: 160 / 200;
          transition: transform 180ms ease, opacity 180ms ease;
          color: inherit;
        }
        .hiw-arch:hover {
          transform: translateY(-2px);
          opacity: 0.92;
        }
        .hiw-arch__content {
          position: absolute;
          inset: 0;
          padding: 14px 10px 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          text-align: center;
          gap: 2px;
        }
        .hiw-arch__icon {
          height: 78px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 4px;
        }
        .hiw-arch__title {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.10em;
          margin-bottom: 2px;
        }
        .hiw-arch__sa {
          font-size: 15px;
          font-weight: 700;
          line-height: 1.15;
          letter-spacing: 0.005em;
          text-shadow: 0 1px 0 rgba(255, 245, 210, 0.5);
        }
        .hiw-arch__tr {
          font-size: 11px;
          font-style: italic;
          font-weight: 600;
          margin-bottom: 2px;
        }
        .hiw-arch__body {
          font-size: 11px;
          font-style: italic;
          line-height: 1.3;
        }

        @media (max-width: 899px) {
          .hiw-desktop { display: none; }
          .hiw-mobile  { display: block; max-width: 460px; margin: 0 auto; }
          [data-testid="homev3-how-it-works"] { padding: 32px 20px 40px !important; }
          .hiw-title { margin-bottom: 24px !important; }
          .hiw-title > *:first-child, .hiw-title > *:last-child { display: none !important; }
          .hiw-title__label { white-space: normal !important; font-size: 11px !important; letter-spacing: 0.14em !important; }
          .hiw-mobile__card {
            display: flex;
            align-items: center;
            gap: 14px;
            padding: 14px 16px;
            background: linear-gradient(180deg, #F5E6C1 0%, #EDD9A3 100%);
            border: 1px solid rgba(201, 168, 76, 0.35);
            border-radius: 12px;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.10);
            transition: transform 150ms ease, box-shadow 150ms ease;
          }
          .hiw-mobile__card:active {
            transform: scale(0.985);
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
          }
          .hiw-mobile__icon {
            flex: 0 0 56px;
            width: 56px;
            height: 56px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .hiw-mobile__text { flex: 1 1 auto; min-width: 0; }
          .hiw-mobile__title {
            font-family: ${F_HEADING};
            font-size: 13px;
            font-weight: 700;
            letter-spacing: 0.10em;
            margin-bottom: 2px;
          }
          .hiw-mobile__sa {
            font-family: ${F_DEVNAG};
            font-size: 17px;
            font-weight: 700;
            line-height: 1.15;
            margin-bottom: 1px;
            text-shadow: 0 1px 0 rgba(255, 245, 210, 0.5);
          }
          .hiw-mobile__tr {
            font-family: ${F_BODY};
            font-size: 11px;
            font-style: italic;
            font-weight: 600;
            margin-bottom: 2px;
          }
          .hiw-mobile__body {
            font-family: ${F_BODY};
            font-size: 12px;
            font-style: italic;
            line-height: 1.35;
          }
          .hiw-mobile__caret {
            display: flex;
            justify-content: center;
            padding: 6px 0;
            opacity: 0.85;
          }
        }
      `}</style>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 4 — EXPLORE POPULAR COLLECTIONS
// (dynamic import() replaced with direct <img src=…>)
// ═══════════════════════════════════════════════════════════════════════════

const COLLECTION_DATA = [
  { title: "Habits",            desc: "Build better habits that last.",           img: A.medHabits  },
  { title: "Focus & Deep Work", desc: "Eliminate distractions. Do what matters.", img: A.medFocus   },
  { title: "Wealth & Money",    desc: "Grow wisely. Live abundantly.",            img: A.medWealth  },
  { title: "Mindset & Growth",  desc: "Think better. Live better.",               img: A.medMindset },
  { title: "Leadership",        desc: "Lead yourself. Then others.",              img: A.medLeader  },
  { title: "Purpose & Life",    desc: "Live with clarity and meaning.",           img: A.medPurpose },
];

function CollectionsSection() {
  return (
    <div data-testid="homev3-collections" className="homev3-collections" style={{ background: "#DFC99A", padding: "36px 48px 56px" }}>
      <div style={{ maxWidth: 940, margin: "0 auto" }}>
        <div className="homev3-collections__title relative flex items-center justify-center gap-4 mb-10">
          <GoldRule />
          <p className="homev3-collections__label m-0" style={{ fontFamily: F_HEADING, fontSize: 13, letterSpacing: "0.18em", color: DARK_BROWN, whiteSpace: "nowrap" }}>
            EXPLORE POPULAR COLLECTIONS
          </p>
          <GoldRule />
          <Link
            to={R.collections}
            data-testid="homev3-collections-view-all"
            className="homev3-collections__view-all absolute right-0 no-underline"
            style={{ fontFamily: F_BODY, fontSize: 13, fontStyle: "italic", color: GOLD, transition: "opacity 150ms" }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.7"; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
          >
            View All
          </Link>
        </div>

        <div className="homev3-collections__grid flex gap-4">
          {COLLECTION_DATA.map((col) => (
            <Link
              key={col.title}
              to={R.collections}
              data-testid={`homev3-collection-${col.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              className="no-underline flex-1 flex flex-col items-center text-center rounded-xl"
              style={{
                // Tweak 2 · warmer card ground so medallions embed richly
                // instead of floating on a pale cream (was #F0E6CC / PARCHMENT_LT).
                background: "#E8D2A0",
                padding:    "20px 10px 18px",
                boxShadow:  "0 4px 12px rgba(44,24,16,0.18)",
                border:     "1px solid rgba(201,168,76,0.35)",
                transition: "opacity 150ms ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
            >
              <img
                src={col.img}
                alt={col.title}
                loading="lazy"
                style={{ width: 72, height: 72, objectFit: "contain", display: "block", marginBottom: 10 }}
              />
              <p className="m-0 mb-1" style={{ fontFamily: F_HEADING, fontSize: 11, fontWeight: 600, color: DARK_BROWN, letterSpacing: "0.04em" }}>
                {col.title}
              </p>
              <p className="m-0" style={{ fontFamily: F_BODY, fontSize: 12, fontStyle: "italic", color: MUTED_BROWN, lineHeight: 1.4 }}>
                {col.desc}
              </p>
            </Link>
          ))}
        </div>
      </div>

      {/* ── Mobile collections (<768px) ──────────────────────────────
             Wrap the 6 medallion cards into a 2-column grid so nothing
             overflows. Title unwraps: gold rules hide, "View All" moves
             below the label. Section side padding drops to 20px. ─── */}
      <style>{`
        @media (max-width: 899px) {
          .homev3-collections { padding: 28px 20px 40px !important; }
          .homev3-collections__title {
            flex-direction: column !important;
            gap: 8px !important;
            margin-bottom: 20px !important;
          }
          .homev3-collections__title > *:first-child,
          .homev3-collections__title > *:nth-child(3) {
            display: none !important;
          }
          .homev3-collections__label {
            white-space: normal !important;
            font-size: 11px !important;
            letter-spacing: 0.14em !important;
            text-align: center;
          }
          .homev3-collections__view-all {
            position: static !important;
            font-size: 12px !important;
          }
          .homev3-collections__grid {
            display: grid !important;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px !important;
          }
        }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 5 — FOOTER BANNER + BOTTOM NAV
// ═══════════════════════════════════════════════════════════════════════════

const BOTTOM_NAV = [
  { label: "Home",         to: R.home        },
  { label: "Library",      to: R.library     },
  { label: "Collections",  to: R.collections },
  { label: "MyCraftz",     to: R.mycraftz    },
  { label: "My Sanctuary", to: R.sanctuary   },
  { label: "Profile",      to: R.profile     },
];

function FooterSection() {
  return (
    <div data-testid="homev3-footer">
      <div
        className="flex items-center justify-between px-12 py-5"
        style={{
          background:   "transparent",
          borderTop:    "1px solid rgba(201,168,76,0.35)",
          borderBottom: "1px solid rgba(201,168,76,0.2)",
        }}
      >
        <span style={{ fontFamily: F_DEVNAG, fontSize: "34px", color: GOLD, lineHeight: 1, flexShrink: 0 }}>
          {"ॐ"}
        </span>

        <p
          className="m-0 text-center flex-1 px-8"
          style={{ fontFamily: F_BODY, fontSize: "16px", fontStyle: "italic", color: DARK_BROWN, lineHeight: 1.6 }}
        >
          &ldquo;Transformation is not a day&rsquo;s work, but a lifetime&rsquo;s art.&rdquo;
        </p>

        <img
          src={A.iconLotus}
          alt="lotus"
          style={{ width: "36px", height: "36px", objectFit: "contain", flexShrink: 0 }}
        />
      </div>

      {/* ── Sitemap band — links every existing page (blog, about, mission,
             partner, for-authors, contact, privacy, terms) so nothing is
             orphaned. Uses the same parchment aesthetic + gold accents. ── */}
      <div
        data-testid="homev3-footer-sitemap"
        style={{
          background: PARCHMENT_LT,
          borderBottom: "1px solid rgba(201,168,76,0.2)",
          padding: "28px 48px 24px",
        }}
      >
        <div className="flex flex-wrap justify-center" style={{ gap: "10px 34px", marginBottom: 18 }}>
          {[
            { label: "Blog",        to: "/blogs" },
            { label: "About",       to: "/about" },
            { label: "Mission",     to: "/mission" },
            { label: "Partner",     to: "/partner" },
            { label: "For Authors", to: "/for-authors" },
            { label: "Contact",     to: "/contact" },
            { label: "Privacy",     to: "/privacy" },
            { label: "Terms",       to: "/terms" },
          ].map(({ label, to }) => (
            <Link
              key={label}
              to={to}
              data-testid={`homev3-footer-sitemap-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className="no-underline"
              style={{
                fontFamily: F_HEADING,
                fontSize: 12,
                letterSpacing: "0.10em",
                color: DARK_BROWN,
                transition: "color 150ms ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = GOLD; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = DARK_BROWN; }}
            >
              {label}
            </Link>
          ))}
        </div>

        <p
          data-testid="homev3-footer-disclaimer"
          className="m-0"
          style={{
            fontFamily: F_BODY,
            fontSize: 12,
            fontStyle: "italic",
            lineHeight: 1.55,
            color: MUTED_BROWN,
            maxWidth: 920,
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <strong style={{ fontStyle: "normal", color: DARK_BROWN }}>Disclaimer:</strong>{" "}
          iCraftz is a behavioural-change engine. Book summaries and insights shown here are original interpretations created for educational purposes only, not reproductions of copyrighted material. All book titles and author names are property of their respective owners. This platform is not affiliated with or endorsed by any publishers or authors. If you are a rights holder and have concerns, please contact us at{" "}
          <a href="mailto:hello@icraftz.com" style={{ color: DARK_BROWN, textDecoration: "underline", textDecorationColor: GOLD }}>hello@icraftz.com</a>{" "}
          and we will promptly address your request.
        </p>
      </div>

      <div
        data-testid="homev3-footer-bottom-nav"
        className="homev3-footer-bottom-nav flex items-center justify-center gap-9 px-10 py-3"
        style={{ background: "rgba(20,10,5,0.85)" }}
      >
        {BOTTOM_NAV.map(({ label, to }) => (
          <Link
            key={label}
            to={to}
            data-testid={`homev3-footer-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className="no-underline homev3-footer-bottom-nav__link"
            style={{ fontFamily: F_HEADING, fontSize: "12px", color: PARCHMENT_LT, letterSpacing: "0.06em", transition: "color 150ms ease" }}
            onMouseEnter={(e) => { e.currentTarget.style.color = GOLD; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = PARCHMENT_LT; }}
          >
            {label}
          </Link>
        ))}
      </div>

      {/* ── Mobile-only overrides for the homepage bottom nav bar ────────
             On mobile the top-nav hamburger already provides full
             navigation to Home / Library / Collections / MyCraftz /
             My Sanctuary / Profile — so duplicating those 6 links in a
             cramped bottom bar (where the global .pt-chrome corner
             scrollwork also overlaps the outer labels) adds noise
             without value. Hide the bottom bar on <768px. Desktop
             keeps the current 6-item centred row.                      */}
      <style>{`
        @media (max-width: 899px) {
          [data-testid="homev3-footer-bottom-nav"] {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ROOT
// ═══════════════════════════════════════════════════════════════════════════

export default function HomeV3Page() {
  return (
    <div className="min-h-screen relative" style={{ background: "transparent" }} data-testid="homev3-root">
      <ParchmentBackground />
      <Navigation />

      {/* Top-right emblem — matches LibraryPage placement exactly
          (width:180, top:8, right:47, z-index:60). Sits above the fixed
          nav (z:50) so its top portion straddles the dark nav bar as an
          intentional design flourish. pointer-events:none keeps the nav
          bell + profile icons clickable through the emblem's transparent
          PNG regions. Hidden on <900px (mobile hero already leads with
          the wordmark). */}
      <img
        src={A.emblem}
        alt="iCRAFTZ emblem — golden book"
        className="homev3-root__emblem absolute pointer-events-none"
        style={{ width: 180, height: "auto", top: 8, right: 47, zIndex: 60 }}
      />
      <style>{`
        @media (max-width: 899px) {
          .homev3-root__emblem { display: none !important; }
        }
      `}</style>

      <HeroSection />
      <HowItWorksSection />
      <CollectionsSection />
      <FooterSection />
    </div>
  );
}

